import { useSendUserInvitationMutation } from '@warebee/frontend/data-access-api-graphql';
import classNames from 'classnames';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useRecoilValue } from 'recoil';
import { Button } from '../../../components/actions/Button';
import { FormErrorField } from '../../../components/FormError';
import { InputEmail } from '../../../components/inputs/InputText';
import { Spacer } from '../../../components/layout/Spacer';
import { ActionBar } from '../../../components/nav/ActionBar';
import { loggedInUser } from '../../../store/auth.state';
import {
  warehouseSelected,
  warehouseUpdateLoadStatus,
} from '../../../store/warehouse.state';
import { WarehouseExtraSettings } from '../../../store/warehouse.types';
import useNotification from '../../../warehouse/hooks/useNotification';
import { DEFAULT_ROLES } from '../../../permissions/roles';

const SettingsInviteUsers: React.FC = () => {
  const { t } = useTranslation(['app', 'errors']);

  const wh = useRecoilValue(warehouseSelected);
  const user = useRecoilValue(loggedInUser);
  const extra: WarehouseExtraSettings = wh?.extraSettings;
  const status = useRecoilValue(warehouseUpdateLoadStatus);
  const feedSettings = extra?.liveFeed;
  // local state
  const [localEnable, localSetEnable] = useState(feedSettings?.enabled);
  const [localJobId, localSetJobId] = useState(feedSettings?.jobId);
  const [localLayoutId, localSetLayoutId] = useState(feedSettings?.layoutId);
  const [email, setEmail] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [invitationLink, setInvitationLink] = useState<string | null>(null);

  useEffect(() => {
    localSetEnable(feedSettings?.enabled);
    localSetJobId(feedSettings?.jobId);
    localSetLayoutId(feedSettings?.layoutId);
  }, [feedSettings]);

  const [sendInvitation] = useSendUserInvitationMutation();
  const [addNotification] = useNotification();

  async function sendInvitationLink() {
    if (!email || isSending) return;

    setIsSending(true);
    setError(null);
    setInvitationLink(null);
    try {
      const result = await sendInvitation({
        variables: {
          input: {
            email,
            warehouse: [
              {
                id: wh.id,
              },
            ],
            featureFlags: {
              role: DEFAULT_ROLES.ManagerWarehouse.id,
            },
            enforceMfa: false,
          },
        },
      });

      if (result.data?.sendUserInvitationLink) {
        const invitationData = result.data.sendUserInvitationLink;
        setEmail('');

        // Handle both new users (with invitation link) and existing users (null invitation link)
        if (invitationData?.invitationLink) {
          // New user - show invitation link
          setInvitationLink(invitationData.invitationLink);
          addNotification({
            type: 'importSuccessful',
            title: t('Invitation sent to {{email}}', { email }),
            details: t(
              'A new user invitation has been created. They will need to use this invitation link to register and access the warehouse.',
            ),
            autoDismiss: true,
          });
        } else {
          // Existing user - no invitation link needed
          setInvitationLink(null);
          addNotification({
            type: 'importSuccessful',
            title: t('User added to warehouse'),
            details: t(
              '{{email}} is an existing user and has been added to this warehouse.',
              { email },
            ),
            autoDismiss: true,
          });
        }
      } else {
        throw new Error(t('Failed to send invitation'));
      }
    } catch (error) {
      console.error('Failed to send invitation:', error);
      const errorMessage =
        error instanceof Error ? error.message : t('Unknown error occurred');
      setError(errorMessage);
      addNotification({
        type: 'importSuccessful',
        title: t('Failed to send invitation'),
        details: errorMessage,
        autoDismiss: true,
      });
    } finally {
      setIsSending(false);
    }
  }

  return (
    <>
      <section
        className={classNames(
          'w-full',
          'mb-4',
          'divide-menu/50 divide-y divide-solid',
          'p-4',
        )}
      >
        <InputEmail
          label={t('Email address')}
          value={email}
          onChange={v => setEmail(v)}
          className="mb-2 p-2"
          placeholder={`manager@example.com`}
          isDisabled={isSending}
        />
        {error && <FormErrorField message={error} />}
        {invitationLink && (
          <div className="bg-menu mt-4 rounded p-4">
            <p className="mb-2 text-sm font-medium">{t('Invitation Link')}:</p>
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={invitationLink}
                readOnly
                className="bg-app-panel flex-1 rounded border-none p-2 text-sm"
              />
              <Button
                label={t('Copy')}
                className="rounded text-xs"
                buttonType="secondary"
                onPress={() => {
                  navigator.clipboard.writeText(invitationLink);
                  addNotification({
                    type: 'importSuccessful',
                    title: t('Link copied to clipboard'),
                    autoDismiss: true,
                  });
                }}
              />
            </div>
          </div>
        )}
      </section>

      <ActionBar
        sticky
        stickyBottom
        className={classNames('flex w-full items-end', 'p-2')}
      >
        <Spacer flexspace />
        <Button
          label={isSending ? t('Sending Invitation...') : t('Send Invitation')}
          buttonType="primary"
          className={classNames('rounded')}
          onPress={() => sendInvitationLink()}
          isDisabled={isSending || !email}
          isLoading={isSending}
        />
      </ActionBar>
    </>
  );
};

export default SettingsInviteUsers;
